import React from "react";

/**
 * Full-screen loading UI that shows the website logo from /public and a message.
 *
 * Usage:
 *   <LoadingScreen companyLanguage={companyLanguage} />
 */
export default function LoadingScreen({
  companyLanguage = "en",
  message,
  logoSrc = `${process.env.PUBLIC_URL || ""}/logobest.png`,
  size = 88,
  className = "",
}) {
  const defaultMessage =
    companyLanguage === "ur" ? "لوڈ ہو رہا ہے..." : "Loading...";

  return (
    <div
      className={[
        "min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center relative overflow-hidden",
        className,
      ].join(" ")}
    >
      {/* soft animated blobs (keeps the same dashboard vibe) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 text-center">
        <div
          className="relative mx-auto"
          style={{ width: size, height: size }}
        >
          {/* spinner ring */}
          <div className="absolute inset-0 animate-spin rounded-full border-4 border-green-200 border-t-green-600"></div>

          {/* logo */}
          <div className="absolute inset-2 rounded-full bg-white shadow-md flex items-center justify-center overflow-hidden">
            <img
              src={logoSrc}
              alt="Logo"
              className="h-full w-full object-contain p-2"
              onError={(e) => {
                // hide broken image, keep spinner + message
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        </div>

        <p
          className={`mt-6 text-gray-700 font-semibold text-lg ${companyLanguage === "ur" ? "font-urdu" : ""
            }`}
        >
          {message || defaultMessage}
        </p>

        <div className="mt-4 flex justify-center gap-2">
          <div
            className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></div>
          <div
            className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          ></div>
          <div
            className="w-2 h-2 bg-teal-500 rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          ></div>
        </div>
      </div>

      {/* keyframes (local so it works anywhere we render this) */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </div>
  );
}


